import { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { useThemeStore } from '@store/themeStore'
import { useTenantStore } from '@store/tenantStore'
import { getAppType, getAdminPanelUrl, getPublicWebsiteUrl } from '@utils/domain'
import { useChatStore, ChatTicket, ChatMessage } from '@store/chatStore'
import { useBrandingStore } from '@store/brandingStore'

// --- Lazy Loaded Components ---
import { PWAUpdater } from '@components/PWAUpdater';
import { AnalyticsTracker } from '@components/AnalyticsTracker';
import { SessionTimeoutModal } from '@components/Common/SessionTimeoutModal';
import { ScrollToTop } from '@components/Common/ScrollToTop';
const AppLayout = lazy(() => import('@components/Layout/AppLayout').then(m => ({ default: m.AppLayout })));
const LoginPage = lazy(() => import('@pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('@pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const IAChatPage = lazy(() => import('@pages/IAChatPage').then(m => ({ default: m.IAChatPage })));
const SettingsPage = lazy(() => import('@pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
// const UsersPage = lazy(() => import('@pages/UsersPage').then(m => ({ default: m.UsersPage })));
const WhatsAppAdminPage = lazy(() => import('@pages/whatsapp/WhatsAppAdminPage').then(m => ({ default: m.WhatsAppAdminPage })));
const LogsPage = lazy(() => import('@pages/LogsPage').then(m => ({ default: m.LogsPage })));
const ProfilePage = lazy(() => import('@pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const WebmailPage = lazy(() => import('./pages/webmail/WebmailPage').then(m => ({ default: m.WebmailPage })));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage').then(m => ({ default: m.CalendarPage })));
const CRMLayout = lazy(() => import('@pages/crm/CRMLayout').then(m => ({ default: m.CRMLayout })));
const CRMIndexRedirect = lazy(() => import('@pages/crm/CRMLayout').then(m => ({ default: m.CRMIndexRedirect })));
const CRMClientesPage = lazy(() => import('@pages/crm/clientes/CRMClientesPage').then(m => ({ default: m.CRMClientesPage })));
const CRMLeadsPage = lazy(() => import('@pages/crm/leads/CRMLeadsPage').then(m => ({ default: m.CRMLeadsPage })));
const CRMContratosPage = lazy(() => import('@pages/crm/contratos/CRMContratosPage').then(m => ({ default: m.CRMContratosPage })));
const CRMAprovacaoPage = lazy(() => import('@pages/crm/aprovacao/CRMAprovacaoPage').then(m => ({ default: m.CRMAprovacaoPage })));
const CRMProspeccaoPage = lazy(() => import('@pages/crm/prospeccao/CRMProspeccaoPage').then(m => ({ default: m.CRMProspeccaoPage })));
const CRMCampanhasPage = lazy(() => import('@pages/crm/campanhas/CRMCampanhasPage').then(m => ({ default: m.CRMCampanhasPage })));
const CRMAlvarasPage = lazy(() => import('@pages/crm/alvaras/CRMAlvarasPage').then(m => ({ default: m.CRMAlvarasPage })));
const CompaniesLayout = lazy(() => import('@pages/companies/CompaniesLayout').then(m => ({ default: m.CompaniesLayout })));
const PatrimonialAdminPage = lazy(() => import('@pages/companies/patrimonial/PatrimonialAdminPage').then(m => ({ default: m.PatrimonialAdminPage })));
const OrganicaAdminPage = lazy(() => import('@pages/companies/organica/OrganicaAdminPage').then(m => ({ default: m.OrganicaAdminPage })));
const AberturaAdminPage = lazy(() => import('@pages/companies/abertura/AberturaAdminPage').then(m => ({ default: m.AberturaAdminPage })));
const EscolaAdminPage = lazy(() => import('@pages/companies/escola/EscolaAdminPage').then(m => ({ default: m.EscolaAdminPage })));
const MonitoramentoAdminPage = lazy(() => import('@pages/companies/monitoramento/MonitoramentoAdminPage').then(m => ({ default: m.MonitoramentoAdminPage })));
const GestaoRegulamentarPage = lazy(() => import('./pages/companies/regulamentar/GestaoRegulamentarPage').then(m => ({ default: m.GestaoRegulamentarPage })));
const DiarioOficialPage = lazy(() => import('./pages/companies/DiarioOficialPage').then(m => ({ default: m.DiarioOficialPage })));
const ChatPage = lazy(() => import('@pages/ChatPage').then(m => ({ default: m.ChatPage })));
const NotFoundPage = lazy(() => import('@pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const TenantEntryPage = lazy(() => import('@pages/TenantEntryPage').then(m => ({ default: m.TenantEntryPage })));
const HRLayout = lazy(() => import('@pages/hr/HRLayout').then(m => ({ default: m.HRLayout })));
const DepartmentsPage = lazy(() => import('@pages/hr/DepartmentsPage').then(m => ({ default: m.DepartmentsPage })));
const JobRolesPage = lazy(() => import('@pages/hr/JobRolesPage').then(m => ({ default: m.JobRolesPage })));
const HRCollaboratorsPage = lazy(() => import('@pages/hr/collaborators/HRCollaboratorsPage').then(m => ({ default: m.HRCollaboratorsPage })));
const HRContractsPage = lazy(() => import('@pages/hr/HRContractsPage').then(m => ({ default: m.HRContractsPage })));
const WebsiteDesignerPage = lazy(() => import('@pages/website/WebsiteDesignerPage').then(m => ({ default: m.WebsiteDesignerPage })));
const PublicWebsitePage = lazy(() => import('./pages/website/PublicWebsitePage'));
const PublicMarketplacePage = lazy(() => import('./pages/website/PublicMarketplacePage'));
const PublicCourseDetailsPage = lazy(() => import('./pages/website/PublicCourseDetailsPage'));
const CoursesEscalasPage = lazy(() => import('./pages/courses/admin/escalas/CoursesEscalasPage').then(m => ({ default: m.CoursesEscalasPage })));
const CoursesAdminLayout = lazy(() => import('./pages/courses/admin/CoursesAdminLayout').then(m => ({ default: m.CoursesAdminLayout })));
const CoursesGradePage = lazy(() => import('./pages/courses/admin/grade/CoursesGradePage').then(m => ({ default: m.CoursesGradePage })));
const CoursesProfessoresPage = lazy(() => import('./pages/courses/admin/professores/CoursesProfessoresPage').then(m => ({ default: m.CoursesProfessoresPage })));
const CoursesCategoriasPage = lazy(() => import('./pages/courses/admin/categorias/CoursesCategoriasPage').then(m => ({ default: m.CoursesCategoriasPage })));
const CoursesBancoQuestoesPage = lazy(() => import('./pages/courses/admin/banco-questoes/CoursesBancoQuestoesPage').then(m => ({ default: m.CoursesBancoQuestoesPage })));
const CoursesCertificadosPage = lazy(() => import('./pages/courses/admin/certificados/CoursesCertificadosPage').then(m => ({ default: m.CoursesCertificadosPage })));
const CoursesVitrinePage = lazy(() => import('./pages/courses/admin/vitrine/CoursesVitrinePage').then(m => ({ default: m.CoursesVitrinePage })));
const CoursesCheckoutPage = lazy(() => import('./pages/courses/admin/checkout/CoursesCheckoutPage').then(m => ({ default: m.CoursesCheckoutPage })));
const LegalTemplatesPage = lazy(() => import('./pages/legal/LegalTemplatesPage').then(m => ({ default: m.LegalTemplatesPage })));

const FinancePage = lazy(() => import('@pages/finance/FinancePage').then(m => ({ default: m.FinancePage })));
const TenantsPage = lazy(() => import('@pages/TenantsPage').then(m => ({ default: m.TenantsPage })));
const SandboxPaymentPage = lazy(() => import('@pages/sandbox/SandboxPaymentPage'));
const ValidateCertificatePage = lazy(() => import('@pages/ValidateCertificatePage').then(m => ({ default: m.ValidateCertificatePage })));

// Importações dos Painéis Admin de Nicho
const MonitoramentoAdminRoutes = lazy(() => import('./pages/companies/monitoramento/ecossistema/painel_admin/routes/index').then(m => ({ default: m.Routes_painel_admin })));
const EscolaAdminRoutes = lazy(() => import('./pages/companies/escola/ecossistema/painel_admin/routes/index').then(m => ({ default: m.Routes_painel_admin })));
const PatrimonialAdminRoutes = lazy(() => import('./pages/companies/patrimonial/ecossistema/painel_admin/routes/index').then(m => ({ default: m.Routes_painel_admin })));
const OrganicaAdminRoutes = lazy(() => import('./pages/companies/organica/ecossistema/painel_admin/routes/index').then(m => ({ default: m.Routes_painel_admin })));


// Importando páginas do Website Institucional Canvas GSP

// Importando páginas do Módulo Hub de Cursos




// Novas importações do Portal do Cliente da Escola de Formação
const PatrimonialClientRoutes = lazy(() => import('./pages/companies/patrimonial/ecossistema/portal_cliente/routes/index').then(m => ({ default: m.PatrimonialClientRoutes })));
const EscolaClientRoutes = lazy(() => import('./pages/companies/escola/ecossistema/portal_cliente/routes/index').then(m => ({ default: m.EscolaClientRoutes })));
const MonitoramentoClientRoutes = lazy(() => import('./pages/companies/monitoramento/ecossistema/portal_cliente/routes/index').then(m => ({ default: m.MonitoramentoClientRoutes })));

const EscolaStudentRoutes = lazy(() => import('./pages/companies/escola/ecossistema/portal_aluno/routes/index').then(m => ({ default: m.EscolaStudentRoutes })));
const EscolaTeacherRoutes = lazy(() => import('./pages/companies/escola/ecossistema/portal_professor/routes/index').then(m => ({ default: m.EscolaTeacherRoutes })));

function ClientPortalRedirect() {
  const user = useAuthStore(s => s.user)
  const activeCompany = useAuthStore(s => s.activeCompany)
  const location = useLocation()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const isEscola = activeCompany?.nichoAtuacao === 'escola_formacao'
  if (isEscola) {
    return <Navigate to="/portal-cliente-escola/dashboard" replace />
  }
  const isMonitoramento = activeCompany?.nichoAtuacao === 'monitoramento_eletronico'
  if (isMonitoramento) {
    return <Navigate to="/portal-cliente-monitoramento/dashboard" replace />
  }

  // Detectar subrota caso o usuário venha de links legados
  const path = location.pathname.toLowerCase()
  let subRoute = 'dashboard'
  if (path.includes('vigilantes')) {
    subRoute = 'vigilantes'
  } else if (path.includes('armamentos')) {
    subRoute = 'armamentos'
  } else if (path.includes('coletes')) {
    subRoute = 'coletes'
  } else if (path.includes('postos')) {
    subRoute = 'postos'
  } else if (path.includes('veiculos') || path.includes('frota')) {
    subRoute = 'veiculos'
  } else if (path.includes('operacoes')) {
    subRoute = 'operacoes'
  }

  return <Navigate to={`/portal-cliente-patrimonial/${subRoute}`} replace />
}

function PrivateRoute({ children }: Readonly<{ children: React.ReactNode }>) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const location = useLocation()
  const isImpersonating = typeof window !== 'undefined' && (
    sessionStorage.getItem('gsp_imp_handshake') !== null ||
    window.location.hash.includes('impersonate') ||
    new URLSearchParams(window.location.search).has('impersonate_token')
  )

  if (!isAuthenticated || isImpersonating) {
    const fullPath = location.pathname + location.search
    if (fullPath && fullPath !== '/' && !fullPath.startsWith('/login')) {
      sessionStorage.setItem('gsp_redirect_after_login', fullPath)
    }
    return <Navigate to={`/login${fullPath && fullPath !== '/' ? `?redirect=${encodeURIComponent(fullPath)}` : ''}`} replace />
  }

  return <>{children}</>
}

const STANDARD_ROLES = new Set(['super_admin', 'admin', 'gestor', 'analyst', 'tecnico', 'viewer', 'funcionario', 'student', 'client', 'cliente', 'professor'])

function resolveAdminDefaultDashboard(): string {
  const tenant = useTenantStore.getState().tenant
  if (tenant && tenant.id !== 'master' && tenant.id !== 'gsp-matriz' && tenant.id !== 'public') {
    const ramo = tenant.ramo_atividade?.toLowerCase() || ''
    return getTenantNicheDashboard(ramo)
  }
  return '/dashboard'
}

function getPerspectiveRedirect(perspective: string | null | undefined, pathname: string): string | null {
  if (
    perspective === 'student' &&
    !pathname.startsWith('/student-portal') &&
    !pathname.startsWith('/portal-aluno-escola') &&
    !pathname.startsWith('/marketplace')
  ) {
    return '/portal-aluno-escola/dashboard'
  }
  if (
    perspective === 'client' &&
    !pathname.startsWith('/client-portal') &&
    !pathname.startsWith('/portal-cliente')
  ) {
    return '/client-portal'
  }
  if (
    perspective === 'professor' &&
    !pathname.startsWith('/teacher-portal') &&
    !pathname.startsWith('/portal-professor-escola')
  ) {
    return '/portal-professor-escola/dashboard'
  }
  return null
}

function getDisallowedRoleRedirect(role: string, perspective: string | null | undefined, pathname: string): string | null {
  if (role === 'student' && perspective === 'student' && !pathname.startsWith('/portal-aluno-escola')) {
    return '/portal-aluno-escola/dashboard'
  }
  if ((role === 'client' || role === 'cliente') && perspective === 'client' && !pathname.startsWith('/client-portal') && !pathname.startsWith('/portal-cliente')) {
    return '/client-portal'
  }
  if (role === 'professor' && perspective === 'professor' && !pathname.startsWith('/portal-professor-escola')) {
    return '/portal-professor-escola/dashboard'
  }
  if (role !== 'student' && role !== 'client' && role !== 'cliente' && role !== 'professor') {
    const targetDash = resolveAdminDefaultDashboard()
    if (pathname !== targetDash) {
      return targetDash
    }
  }
  return null
}

function RoleGuard({ children, allowedRoles }: Readonly<{ children: React.ReactNode; allowedRoles: string[] }>) {
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const location = useLocation()
  
  if (!user) {
    return <Navigate to="/login" replace />
  }

  const perspectiveRedirect = getPerspectiveRedirect(activePerspective, location.pathname)
  if (perspectiveRedirect) {
    return <Navigate to={perspectiveRedirect} replace />
  }
  
  const isCustomBackofficeRole = !STANDARD_ROLES.has(user.role)
  if (!allowedRoles.includes(user.role) && !isCustomBackofficeRole) {
    const redirectPath = getDisallowedRoleRedirect(user.role, activePerspective, location.pathname)
    return redirectPath ? <Navigate to={redirectPath} replace /> : <NotFoundPage />
  }
  
  return <>{children}</>
}

function hasStudentPermission(role: string, permissions: string[] | undefined, requiredPermission?: string): boolean {
  if (!requiredPermission || role === 'super_admin' || role === 'admin') {
    return true
  }
  if (role === 'student' && requiredPermission === 'student_portal:access') {
    return true
  }
  return !!permissions?.includes(requiredPermission)
}

function StudentGuard({ children, requiredPermission }: Readonly<{ children: React.ReactNode; requiredPermission?: string }>) {
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const location = useLocation()
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (activePerspective === 'admin' && user.role !== 'super_admin' && user.role !== 'admin') {
    const targetDash = resolveAdminDefaultDashboard()
    if (location.pathname !== targetDash) {
      return <Navigate to={targetDash} replace />
    }
  }
  if (activePerspective === 'client' && !location.pathname.startsWith('/client-portal') && !location.pathname.startsWith('/portal-cliente')) {
    return <Navigate to="/client-portal" replace />
  }
  
  if (!hasStudentPermission(user.role, user.permissions, requiredPermission)) {
    if (location.pathname === '/portal-aluno-escola/dashboard') {
      return <NotFoundPage />
    }
    return <Navigate to="/portal-aluno-escola/dashboard" replace />
  }
  
  return <>{children}</>
}

function TeacherGuard({ children }: Readonly<{ children: React.ReactNode }>) {
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const location = useLocation()
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (user.role === 'super_admin' || user.role === 'admin' || user.role === 'professor') {
    return <>{children}</>
  }

  // Bloqueia acesso a rotas de professor se a perspectiva atual for outra
  if (activePerspective !== 'professor') {
    if (activePerspective === 'student' && !location.pathname.startsWith('/portal-aluno-escola')) {
      return <Navigate to="/portal-aluno-escola/dashboard" replace />
    }
    if (activePerspective === 'client' && !location.pathname.startsWith('/client-portal') && !location.pathname.startsWith('/portal-cliente')) {
      return <Navigate to="/client-portal" replace />
    }
    const targetDash = resolveAdminDefaultDashboard()
    if (location.pathname !== targetDash) {
      return <Navigate to={targetDash} replace />
    }
    return <NotFoundPage />
  }
  
  return <>{children}</>
}

function getTenantNicheDashboard(ramo: string): string {
  if (ramo === 'patrimonial') {
    return '/admin-patrimonial/dashboard'
  }
  if (ramo.includes('escola')) {
    return '/admin-escola/dashboard'
  }
  if (ramo === 'monitoramento_eletronico') {
    return '/admin-monitoramento/dashboard'
  }
  return '/dashboard'
}

function getNonClientRedirectPath(perspective: string | null | undefined, pathname: string): string | null {
  if (perspective === 'student' && !pathname.startsWith('/portal-aluno-escola')) {
    return '/portal-aluno-escola/dashboard'
  }
  
  const tenant = useTenantStore.getState().tenant
  if (tenant && tenant.id !== 'master' && tenant.id !== 'gsp-matriz' && tenant.id !== 'public') {
    const ramo = tenant.ramo_atividade?.toLowerCase() || ''
    const targetDashboard = getTenantNicheDashboard(ramo)
    const basePrefix = targetDashboard.replace('/dashboard', '')
    if (targetDashboard !== '/dashboard' && !pathname.startsWith(basePrefix)) {
      return targetDashboard
    }
  }
  
  return pathname !== '/dashboard' ? '/dashboard' : null
}

function ClientGuard({ children }: Readonly<{ children: React.ReactNode }>) {
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const location = useLocation()
  
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Clientes reais sempre têm acesso garantido ao portal do cliente
  if (user.role === 'client' || user.role === 'cliente') {
    return <>{children}</>
  }

  // Admins e super_admins sempre podem visualizar qualquer portal de cliente diretamente
  if (user.role === 'super_admin' || user.role === 'admin') {
    return <>{children}</>
  }
  
  if (activePerspective !== 'client') {
    const redirectPath = getNonClientRedirectPath(activePerspective, location.pathname)
    return redirectPath ? <Navigate to={redirectPath} replace /> : <NotFoundPage />
  }
  
  return <>{children}</>
}

function checkPbacAccess(userPerms: string[], requiredPermission?: string, requiredPermissions?: string[]): boolean {
  if (requiredPermission) {
    const hasSingle = requiredPermission === 'companies:read'
      ? userPerms.includes('companies:read') || userPerms.some(p => p.startsWith('companies:'))
      : userPerms.includes(requiredPermission)
    if (hasSingle) {
      return true
    }
  }
  if (requiredPermissions && requiredPermissions.length > 0) {
    return requiredPermissions.some(p => userPerms.includes(p))
  }
  return false
}

function isRoleAllowed(role: string, allowedRoles?: string[]): boolean {
  if (!allowedRoles) {
    return true
  }
  const isCustomBackofficeRole = !['super_admin', 'admin', 'gestor', 'analyst', 'tecnico', 'viewer', 'student', 'client'].includes(role)
  return allowedRoles.includes(role) || isCustomBackofficeRole
}

function PermissionGuard({ children, requiredPermission, requiredPermissions, allowedRoles }: Readonly<{ children: React.ReactNode; requiredPermission?: string; requiredPermissions?: string[]; allowedRoles?: string[] }>) {
  const user = useAuthStore((s) => s.user)
  const activePerspective = useAuthStore((s) => s.activePerspective)
  const location = useLocation()
  
  if (!user) {
    return <Navigate to="/login" replace />
  }

  const perspectiveRedirect = getPerspectiveRedirect(activePerspective, location.pathname)
  if (perspectiveRedirect) {
    return <Navigate to={perspectiveRedirect} replace />
  }

  // Super admin sempre tem acesso irrestrito
  if (user.role === 'super_admin') {
    return <>{children}</>
  }

  const userPerms = user.permissions || []
  const hasCustomPbac = userPerms.length > 0
  const hasRouteRequirement = Boolean(requiredPermission || (requiredPermissions && requiredPermissions.length > 0))

  // 1. Soberania do PBAC: Se a rota exige permissão específica
  if (hasRouteRequirement) {
    if (checkPbacAccess(userPerms, requiredPermission, requiredPermissions)) {
      return <>{children}</>
    }

    // Se o usuário possui PBAC configurado e NÃO tem a permissão, bloqueia o acesso
    if (hasCustomPbac) {
      const fallbackPath = getNonClientRedirectPath(activePerspective, location.pathname)
      return fallbackPath ? <Navigate to={fallbackPath} replace /> : <NotFoundPage />
    }
  }

  // 2. Fallback de Cargo (RBAC legado / inicial)
  if (!isRoleAllowed(user.role, allowedRoles)) {
    const fallbackPath = getNonClientRedirectPath(activePerspective, location.pathname)
    return fallbackPath ? <Navigate to={fallbackPath} replace /> : <NotFoundPage />
  }

  return <>{children}</>
}

export default function App() {
  const theme = useThemeStore((s) => s.theme)
  const branding = useBrandingStore()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { tenant, isLoading: isTenantLoading, resolveTenant } = useTenantStore()
  
  const updateInstance = useChatStore(s => s.updateInstance)
  const addMessage = useChatStore(s => s.addMessage)
  const addTicket = useChatStore(s => s.addTicket)
  const updateTicket = useChatStore(s => s.updateTicket)
  const removeTicket = useChatStore(s => s.removeTicket)
  const removeInstance = useChatStore(s => s.removeInstance)
  const setPresence = useChatStore(s => s.setPresence)

  // ── Auto-Update PWA ──
  // A lógica de atualização foi movida para o componente PWAUpdater para permitir que o usuário controle o recarregamento.

  useEffect(() => {
    void resolveTenant()
  }, [resolveTenant])

  useEffect(() => {
    // Força o subdomínio app. para o painel administrativo
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (getAppType() === 'admin') {
      if (hostname === 'localhost' && port === '3673') {
        window.location.replace(`http://app.localhost:3673${window.location.pathname}${window.location.search}`);
      } else if (hostname === 'gspassessoria.com.br' || hostname === 'www.gspassessoria.com.br') {
        // Embora getAppType retorne 'website' para esses, se por algum motivo 
        // for forçado como admin, garante o redirecionamento.
        window.location.replace(`https://app.gspassessoria.com.br${window.location.pathname}${window.location.search}`);
      }
    }
  }, [])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  useEffect(() => {
    // Sincroniza silenciosamente o canal de Push Notifications do dispositivo com o backend
    import('@services/push.service')
      .then(m => m.pushService.autoSync())
      .catch(() => {
        // Ignora falha silenciosa de auto-sync push
      })
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      const syncUserProfile = async () => {
        try {
          const { authService } = await import('@services/authService')
          const user = await authService.me()
          useAuthStore.getState().setUser(user)
        } catch (err) {
          console.error('Failed to sync user profile:', err)
        }
      }
      void syncUserProfile()
    }
  }, [isAuthenticated])

  useEffect(() => {
    const primary = tenant?.settings?.primaryColor || branding.primaryColor
    const secondary = tenant?.settings?.secondaryColor || branding.secondaryColor
    const titleName = tenant?.nome_empresa || branding.systemName
    const finalFavicon = tenant?.settings?.faviconUrl || branding.faviconUrl || '/favicon.ico'

    const root = document.documentElement
    root.style.setProperty('--color-brand-primary', primary)
    root.style.setProperty('--color-brand-secondary', secondary)
    root.style.setProperty('--color-brand-accent', primary)
    
    // Gradiente dinâmico
    root.style.setProperty('--color-brand-gradient', `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`)
    
    // Brilhos, sombras e bordas dinâmicos baseados nas cores customizadas (com opacidade segura em hex)
    root.style.setProperty('--color-gold-glow', `${primary}59`)
    root.style.setProperty('--shadow-brand', `0 4px 20px ${primary}40`)
    root.style.setProperty('--color-border', `${primary}26`)
    
    // Atualiza o favicon do navegador de forma confiável (removendo a tag antiga e criando nova)
    const oldLink = document.querySelector("link[rel~='icon']")
    if (oldLink) {
      oldLink.remove()
    }
    const newLink = document.createElement('link')
    newLink.rel = 'icon'
    newLink.href = finalFavicon
    document.getElementsByTagName('head')[0].appendChild(newLink)
    
    // Atualiza o título da página do navegador
    if (getAppType() === 'website') {
      document.title = titleName
    } else {
      document.title = `${titleName} | Painel`
    }
  }, [branding, tenant])

  useEffect(() => {
    if (!isAuthenticated) {
      void import('@services/socket.service').then(({ socketService }) => {
        socketService.disconnect()
      }).catch(err => {
        console.error('Failed to disconnect socket:', err)
      })
      return
    }

    let active = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let socketServiceRef: any = null

    const initSocket = async () => {
      try {
        const { socketService } = await import('@services/socket.service')
        if (!active) { return }
        socketServiceRef = socketService
        socketService.connect()

        const { chatService } = await import('@services/chat.service')
        const { requestNotificationPermission, sendNativeNotification } = await import('@utils/notification')

        try {
          const tickets = await chatService.listTickets()
          if (active) {
            useChatStore.getState().setTickets(tickets)
            const granted = await requestNotificationPermission()
            if (granted && active) {
              const currentUserId = useAuthStore.getState().user?.id
              const unreadTickets = tickets.filter(t => t.unread_count > 0 && t.status !== 'closed' && t.assigned_to === currentUserId)
              if (unreadTickets.length > 0) {
                const totalUnread = unreadTickets.reduce((acc, t) => acc + t.unread_count, 0)
                void sendNativeNotification(`Você tem ${totalUnread} nova(s) mensagem(ns) não lida(s)`, {
                  body: 'Toque aqui para abrir o chat.',
                  url: '/chat'
                })
              }
            }
          }
        } catch (fetchErr) {
          console.error('Failed to fetch initial tickets', fetchErr)
        }

        socketService.on('whatsapp_connection', (data: { instance: string, status: 'connected' | 'disconnected' | 'connecting' }) => {
          updateInstance(data.instance, { status: data.status })
        })

        socketService.on('whatsapp_qrcode', (data: { instance: string, qrcode: string }) => {
          updateInstance(data.instance, { qr_code: data.qrcode, status: 'connecting' })
        })

        socketService.on('whatsapp_deleted', (data: { instance: string }) => {
          removeInstance(data.instance)
        })

        void requestNotificationPermission()

        socketService.on('chat_ticket_new', (ticket: ChatTicket) => {
          addTicket(ticket)
          const currentUserId = useAuthStore.getState().user?.id
          if (ticket.assigned_to && ticket.assigned_to === currentUserId) {
            void sendNativeNotification(`Novo Atendimento: ${ticket.contact_name || ticket.contact_number}`, {
              body: `Contato: ${ticket.contact_number}`,
              url: '/chat'
            })
          }
        })

        socketService.on('chat_ticket_updated', (ticket: ChatTicket) => {
          updateTicket(ticket.id, ticket)
        })

        socketService.on('chat_ticket_deleted', (ticket: ChatTicket) => {
          removeTicket(ticket.id)
        })

        socketService.on('chat_message_new', (data: { ticketId: string, message: ChatMessage }) => {
          addMessage(data.ticketId, data.message)
          setPresence(data.ticketId, null)
          if (data.message.sender_type === 'contact') {
            const currentUserId = useAuthStore.getState().user?.id
            const ticket = useChatStore.getState().tickets.find(t => t.id === data.ticketId)
            if (ticket?.contact_number) {
              setPresence(ticket.contact_number, null)
            }
            if (ticket?.assigned_to === currentUserId) {
              const contactName = ticket.contact_name || ticket.contact_number || data.message.sender_name || 'Cliente'
              void sendNativeNotification(`Nova mensagem de ${contactName}`, {
                body: data.message.content || 'Nova mensagem no chat',
                url: '/chat'
              })
            }
          }
        })

        socketService.on('chat_presence', (data: { ticketId?: string, contactNumber?: string, presence: 'composing' | 'recording' | 'paused' }) => {
          if (data.ticketId) {
            setPresence(data.ticketId, data.presence)
          }
          if (data.contactNumber) {
            setPresence(data.contactNumber, data.presence)
          }
        })
      } catch (err) {
        console.error('Failed to initialize socket service:', err)
      }
    }

    void initSocket()

    return () => {
      active = false
      if (socketServiceRef) {
        socketServiceRef.off('whatsapp_connection')
        socketServiceRef.off('whatsapp_qrcode')
        socketServiceRef.off('whatsapp_deleted')
        socketServiceRef.off('chat_ticket_new')
        socketServiceRef.off('chat_ticket_updated')
        socketServiceRef.off('chat_ticket_deleted')
        socketServiceRef.off('chat_message_new')
        socketServiceRef.off('chat_presence')
      }
    }
  }, [isAuthenticated, updateInstance, removeInstance, addTicket, updateTicket, removeTicket, addMessage, setPresence])

  if (isTenantLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-base)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  // O appType agora não vai ditar tudo, pois o Tenant dita se somos public ou tenant isolado.
  // No momento, manteremos compatibilidade com o Website / Admin atual.
  const appType = getAppType()

  if (appType === 'website') {
    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <PWAUpdater />
        <AnalyticsTracker />
        <Suspense fallback={<LoadingFallback />}><Routes>
          {/* Rotas Públicas do Website Institucional Canvas GSP */}
          <Route path="/" element={<PublicWebsitePage />} />
          <Route path="/site/:slug" element={<PublicWebsitePage />} />
          <Route path="/loja" element={<PublicMarketplacePage />} />
          <Route path="/loja/curso/:id" element={<PublicCourseDetailsPage />} />
          <Route path="/validar-certificado" element={<ValidateCertificatePage />} />
          <Route path="/validar-certificado/:code" element={<ValidateCertificatePage />} />

          {/* Qualquer outra rota no Website redireciona para o Admin Panel correspondente */}
          <Route path="*" element={<RedirectToAdmin />} />
        </Routes></Suspense>
      </BrowserRouter>
    )
  }

  // Se for o Admin Panel
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <PWAUpdater />
      <AnalyticsTracker />
      <SessionTimeoutModal />
      <Suspense fallback={<LoadingFallback />}><Routes>
        {/* Redirecionamento da raiz do Admin: se autenticado vai para /dashboard, senão para /login */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />

        {/* Login Público */}
        <Route path="/login" element={
          isAuthenticated && !(
            sessionStorage.getItem('gsp_imp_handshake') !== null ||
            window.location.hash.includes('impersonate') ||
            new URLSearchParams(window.location.search).has('impersonate_token')
          ) ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } />
        
        {/* Redefinição Pública de Senha */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Sandbox Payment Simulator (Public) */}
        <Route path="/sandbox/:method/:id" element={<SandboxPaymentPage />} />

        {/* Validação Pública de Certificados PF */}
        <Route path="/validar-certificado" element={<ValidateCertificatePage />} />
        <Route path="/validar-certificado/:code" element={<ValidateCertificatePage />} />

        {/* Private Admin Workspace */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        >
          <Route path="dashboard" element={
            <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico']}>
              <DashboardPage />
            </RoleGuard>
          } />
          <Route path="hr" element={
            <PermissionGuard requiredPermission="hr:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
              <HRLayout />
            </PermissionGuard>
          }>
            <Route index element={<Navigate to="departments" replace />} />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="roles" element={<JobRolesPage />} />
            <Route path="collaborators" element={<HRCollaboratorsPage />} />
            <Route path="payroll" element={<Navigate to="/finance/folha-pagamento" replace />} />
            <Route path="contracts" element={<HRContractsPage />} />
          </Route>

          <Route path="ia-chat" element={
            <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico']}>
              <IAChatPage />
            </RoleGuard>
          } />
          <Route path="crm" element={
            <PermissionGuard 
              requiredPermissions={['crm:read', 'crm:clientes:read', 'crm:leads:read', 'crm:contratos:read', 'crm:aprovacao:read', 'crm:prospeccao:read', 'crm:campanhas:read', 'crm:alvaras:read']} 
              allowedRoles={['super_admin', 'admin', 'gestor']}
            >
              <CRMLayout />
            </PermissionGuard>
          }>
            <Route index element={<CRMIndexRedirect />} />
            <Route path="clientes" element={<CRMClientesPage />} />
            <Route path="leads" element={<CRMLeadsPage />} />
            <Route path="contratos" element={<CRMContratosPage />} />
            <Route path="aprovacao" element={<CRMAprovacaoPage />} />
            <Route path="prospeccao" element={<CRMProspeccaoPage />} />
            <Route path="campanhas" element={<CRMCampanhasPage />} />
            <Route path="alvaras" element={<CRMAlvarasPage />} />
          </Route>
          <Route path="companies" element={
            <PermissionGuard requiredPermission="companies:read" allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'professor']}>
              <CompaniesLayout />
            </PermissionGuard>
          }>
            <Route index element={<Navigate to="patrimonial" replace />} />
            <Route path="abertura" element={<AberturaAdminPage />} />
            <Route path="patrimonial" element={<PatrimonialAdminPage />} />
            <Route path="organica" element={<OrganicaAdminPage />} />
            <Route path="escola" element={<EscolaAdminPage />} />
            <Route path="monitoramento" element={<MonitoramentoAdminPage />} />
            <Route path="tenants" element={
              <PermissionGuard requiredPermission="companies:tenants:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <TenantsPage />
              </PermissionGuard>
            } />
            <Route path="regulamentar" element={
              <PermissionGuard requiredPermission="companies:regulamentar:read" allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'professor']}>
                <GestaoRegulamentarPage />
              </PermissionGuard>
            } />
            <Route path="diario-oficial" element={<DiarioOficialPage />} />
          </Route>
          <Route path="chat" element={
            <PermissionGuard requiredPermission="chat:access" allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'professor']}>
              <ChatPage />
            </PermissionGuard>
          } />
          <Route path="finance/*" element={
            <PermissionGuard requiredPermission="finance:read" allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'professor']}>
              <FinancePage />
            </PermissionGuard>
          } />
          <Route path="website-designer" element={
            <PermissionGuard requiredPermission="website_designer:access" allowedRoles={['super_admin', 'admin']}>
              <WebsiteDesignerPage />
            </PermissionGuard>
          } />
          <Route path="settings" element={
            <PermissionGuard requiredPermission="settings:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
              <SettingsPage />
            </PermissionGuard>
          } />
          <Route path="/system/settings" element={
            <PermissionGuard requiredPermission="settings:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
              <SettingsPage />
            </PermissionGuard>
          } />


          <Route path="whatsapp-admin" element={
            <PermissionGuard requiredPermission="settings:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
              <WhatsAppAdminPage />
            </PermissionGuard>
          } />
          <Route path="system-logs" element={
            <PermissionGuard requiredPermission="settings:logs:read" allowedRoles={['super_admin', 'admin']}>
              <LogsPage />
            </PermissionGuard>
          } />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="webmail" element={<WebmailPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          
          {/* Módulo Jurídico */}
          <Route path="legal/templates" element={
            <PermissionGuard requiredPermission="settings:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
              <LegalTemplatesPage />
            </PermissionGuard>
          } />
          
          {/* Rotas do Módulo Hub de Cursos (Modularizado) */}
          <Route path="courses-admin" element={
            <PermissionGuard requiredPermissions={['courses:read', 'courses:matriculas:read', 'courses:professores:read', 'courses:categorias:read', 'courses:questions:read', 'courses:certificados:read', 'courses:vitrine:read', 'courses:checkout:read', 'courses:escalas:read']} allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'professor']}>
              <CoursesAdminLayout />
            </PermissionGuard>
          }>
            <Route index element={<Navigate to="grade" replace />} />
            <Route path="grade" element={<CoursesGradePage />} />
            <Route path="matriculas" element={<Navigate to="/courses-admin/grade?tab=matriculas" replace />} />
            <Route path="professores" element={
              <PermissionGuard requiredPermissions={['courses:professores:read', 'courses:read']} allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesProfessoresPage />
              </PermissionGuard>
            } />
            <Route path="categorias" element={
              <PermissionGuard requiredPermission="courses:categorias:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesCategoriasPage />
              </PermissionGuard>
            } />
            <Route path="banco-questoes" element={
              <PermissionGuard requiredPermission="courses:questions:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesBancoQuestoesPage />
              </PermissionGuard>
            } />
            <Route path="certificados" element={
              <PermissionGuard requiredPermission="courses:certificados:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesCertificadosPage />
              </PermissionGuard>
            } />
            <Route path="escalas" element={<CoursesEscalasPage />} />
            <Route path="vitrine" element={
              <PermissionGuard requiredPermission="courses:vitrine:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesVitrinePage />
              </PermissionGuard>
            } />
            <Route path="checkout" element={
              <PermissionGuard requiredPermission="courses:checkout:read" allowedRoles={['super_admin', 'admin', 'gestor']}>
                <CoursesCheckoutPage />
              </PermissionGuard>
            } />
          </Route>
          {/* Legacy fallback client routes (just redirecting to new dynamic ones) */}
          <Route path="client" element={<ClientPortalRedirect />} />
          <Route path="client/*" element={<ClientPortalRedirect />} />
          <Route path="client-portal" element={<ClientPortalRedirect />} />
          <Route path="client-portal/*" element={<ClientPortalRedirect />} />
          <Route path="student" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
          <Route path="student/*" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
          <Route path="student-portal" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
          <Route path="student-portal/*" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
          <Route path="professor" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
          <Route path="professor/*" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
          <Route path="teacher-portal" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
          <Route path="teacher-portal/*" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
        </Route>

        {/* Rotas de alias de topo para portais */}
        <Route path="client" element={<ClientPortalRedirect />} />
        <Route path="client/*" element={<ClientPortalRedirect />} />
        <Route path="client-portal/*" element={<ClientPortalRedirect />} />
        <Route path="student" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
        <Route path="student/*" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
        <Route path="student-portal/*" element={<Navigate to="/portal-aluno-escola/dashboard" replace />} />
        <Route path="professor" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
        <Route path="professor/*" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />
        <Route path="teacher-portal/*" element={<Navigate to="/portal-professor-escola/dashboard" replace />} />

        {/* Niche Admin Routes */}
        <Route path="admin-monitoramento/*" element={
          <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico']}>
            <MonitoramentoAdminRoutes />
          </RoleGuard>
        } />
        <Route path="admin-escola/*" element={
          <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico', 'funcionario', 'viewer']}>
            <EscolaAdminRoutes />
          </RoleGuard>
        } />
        <Route path="admin-patrimonial/*" element={
          <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico']}>
            <PatrimonialAdminRoutes />
          </RoleGuard>
        } />
        <Route path="admin-organica/*" element={
          <RoleGuard allowedRoles={['super_admin', 'admin', 'gestor', 'analyst', 'tecnico']}>
            <OrganicaAdminRoutes />
          </RoleGuard>
        } />

        {/* Niche Client Portals */}
        <Route path="portal-cliente-patrimonial/*" element={<ClientGuard><PatrimonialClientRoutes /></ClientGuard>} />
        <Route path="portal-cliente-escola/*" element={<ClientGuard><EscolaClientRoutes /></ClientGuard>} />
        <Route path="portal-cliente-monitoramento/*" element={<ClientGuard><MonitoramentoClientRoutes /></ClientGuard>} />

        <Route path="portal-aluno-escola/*" element={<StudentGuard requiredPermission="student_portal:access"><EscolaStudentRoutes /></StudentGuard>} />
        <Route path="portal-professor-escola/*" element={<TeacherGuard><EscolaTeacherRoutes /></TeacherGuard>} />

        {/* Rotas públicas tentadas no Admin redirecionam para o Website correspondente */}
        <Route path="/site/:slug" element={<RedirectToWebsite />} />
        <Route path="/loja" element={<RedirectToWebsite />} />
        <Route path="/loja/curso/:id" element={<RedirectToWebsite />} />

        {/* Rotas de Tenant SaaS White-Label por Path */}
        <Route path="/:slug/login" element={<LoginPage />} />
        <Route path="/:slug" element={<TenantEntryPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes></Suspense>
    </BrowserRouter>
  )
}

// ── Componentes de Redirecionamento Dinâmico entre Portas/Subdomínios ──

function RedirectToAdmin() {
  const location = useLocation()
  useEffect(() => {
    window.location.href = getAdminPanelUrl(location.pathname + location.search)
  }, [location])
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#0b0c10',
      color: '#ffffff',
      fontFamily: 'sans-serif'
    }}>
      <h2 style={{ marginBottom: 10, fontSize: '20px', fontWeight: 600 }}>Redirecionando para a área de login...</h2>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Por favor, aguarde um instante.</p>
    </div>
  )
}

function RedirectToWebsite() {
  const location = useLocation()
  useEffect(() => {
    window.location.href = getPublicWebsiteUrl(location.pathname + location.search)
  }, [location])
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: '#0b0c10',
      color: '#ffffff',
      fontFamily: 'sans-serif'
    }}>
      <h2 style={{ marginBottom: 10, fontSize: '20px', fontWeight: 600 }}>Redirecionando para o portal institucional...</h2>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Por favor, aguarde um instante.</p>
    </div>
  )
}




function LoadingFallback() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-base, #0b0c10)' }}>
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
    </div>
  )
}
